import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Loader2, Zap } from 'lucide-react';
import { adminClientPaymentsAPI } from '../../services/api';

/*
 * The tenant's AutoPay charging switch (Phase 7e), on Portal Settings →
 * Payments. Saved on its own — not with the rest of the page — because it
 * decides whether customers' saved cards and bank accounts are charged
 * without anybody pressing a button, and turning it on is audited.
 */
export default function AutopayChargingCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['autopay-charging-settings'],
    queryFn: () => adminClientPaymentsAPI.getAutopayChargingSettings(),
  });
  const stored = data?.data;
  const [hour, setHour] = useState(6);
  useEffect(() => {
    if (stored) setHour(stored.chargeHour ?? 6);
  }, [stored]);

  const mutation = useMutation({
    mutationFn: (body) => adminClientPaymentsAPI.saveAutopayChargingSettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autopay-charging-settings'] });
      queryClient.invalidateQueries({ queryKey: ['autopay-preview'] });
    },
  });

  const toggle = () => {
    const turningOn = !stored?.chargingEnabled;
    const message = turningOn
      ? 'Turn on AutoPay charging?\n\nFrom the next daily run, customers who have AutoPay on will have their saved payment method charged for invoices that are due, without anyone pressing a button. Check the preview under Admin → Client Payments first.'
      : 'Turn off AutoPay charging? The daily run will only preview from now on. Customers keep their AutoPay settings.';
    if (window.confirm(message)) mutation.mutate({ chargingEnabled: turningOn, chargeHour: Number(hour) });
  };

  const saveHour = () => mutation.mutate({ chargeHour: Number(hour) });
  const on = Boolean(stored?.chargingEnabled);

  return (
    <div id="autopay" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-neutral-400" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">AutoPay Charging</h2>
        </div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Customers can turn AutoPay on and choose a payment method, a maximum and a day of the month. Nothing is charged
          until you switch charging on here. While it is off, the daily run only works out what it would charge — see it under{' '}
          <Link to="/admin/client-payments" className="text-primary-600 hover:underline">Admin → Client Payments</Link>.
        </p>
      </div>
      <div className="p-6 space-y-5">
        {isLoading && <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />}
        {error && <p className="text-sm text-red-600">{error.response?.data?.error || 'Could not load the AutoPay setting.'}</p>}
        {stored && (
          <>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Charge customers automatically</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {on
                    ? 'On. Due invoices are charged to each customer\'s chosen method once a day.'
                    : 'Off. Preview only — no customer is charged.'}
                </p>
              </div>
              <button
                type="button"
                onClick={toggle}
                disabled={mutation.isPending}
                role="switch"
                aria-checked={on}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${on ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            <div>
              <label htmlFor="autopay-hour" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Run each day at
              </label>
              <div className="flex items-center gap-3">
                <select
                  id="autopay-hour"
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
                {Number(hour) !== Number(stored.chargeHour) && (
                  <button type="button" onClick={saveHour} disabled={mutation.isPending} className="px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                    Save time
                  </button>
                )}
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                In the portal&apos;s timezone. Each company&apos;s invoices are judged due against that company&apos;s own date.
              </p>
            </div>

            {on && (
              <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Declines, charges whose outcome is unclear and runs over a customer&apos;s maximum raise Payment Alerts on the dashboard.
                  A charge marked &quot;Needs review&quot; stops AutoPay for that company until you resolve it.
                </span>
              </div>
            )}
            {mutation.isSuccess && (
              <p className="text-sm text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Saved
              </p>
            )}
            {mutation.isError && <p className="text-sm text-red-600">{mutation.error?.response?.data?.error || 'Could not save'}</p>}
          </>
        )}
      </div>
    </div>
  );
}
